import React, {FC, useEffect, useState} from 'react';
import {useTheme} from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import SwipeableViews from 'react-swipeable-views';
import {Box, DialogProps, Typography} from '@material-ui/core';
import {withLocalization} from 'epic-localization/src/context/LocalizationContext';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import Stepper from './Stepper';
import {getDirectionStyles} from '../../utils/ThemeUtils';
import {
    CloseButton,
    Dialog,
    DialogActions,
    DialogButton,
    DialogContent,
    StepWrapper
} from './TourModal.styles';

const CODES = {
    next: 'epic.affiliate.fortnite_games.tour_modal.next',
    skip: 'epic.affiliate.fortnite_games.tour_modal.skip'
};

export interface Step {
    title: string;
    description: string;
    image: string;
}

export interface TourModalProps extends DialogProps {
    steps: Step[];
    initialStep?: number;

    onClose: () => void;
    onComplete: () => void;
    onSkip: () => void;

    getMessage: (code: string) => string;
}

const TourModal: FC<TourModalProps> = ({
    steps,
    initialStep = 0,
    onClose,
    onComplete,
    onSkip,
    getMessage,
    ...props
}: TourModalProps) => {
    const [currentStep, setCurrentStep] = useState(initialStep);
    const isLastStep = currentStep === steps.length - 1;
    const theme = useTheme();
    const isMdBreakpoint = useMediaQuery(theme.breakpoints.down('md'));

    const handleNext = () => {
        if (isLastStep) {
            onComplete();
        } else {
            setCurrentStep(current => current + 1);
        }
    };

    useEffect(() => {
        if (props.open) {
            setCurrentStep(initialStep);
        }
    }, [props.open]);
    /* eslint-env browser */
    /* Preload slides */
    useEffect(() => {
        steps.forEach(({image}) => {
            const img = new Image();

            img.src = image;
        });
    }, [steps]);

    return (
        <Dialog {...props} maxWidth="md">
            <CloseButton
                aria-label="close"
                variant="text"
                component="div"
                color="primary"
                startIcon={<CloseIcon fontSize="small" viewBox="0 0 22 24" />}
                onClick={onClose}
            />
            <DialogContent>
                <Box
                    height="100%"
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="space-between">
                    <div>
                        <Typography variant="h4" color="textPrimary" align="center">
                            {steps[currentStep].title}
                        </Typography>
                        <Box mt={4} />
                        <Typography variant="body1" color="textSecondary" align="center">
                            {steps[currentStep].description}
                        </Typography>
                    </div>
                    <SwipeableViews
                        disabled={!isMdBreakpoint}
                        index={currentStep}
                        axis={getDirectionStyles(theme.direction)('x', 'x-reverse')}
                        onChangeIndex={setCurrentStep}
                        enableMouseEvents>
                        {steps.map(({title, image}, index) => (
                            <div key={title}>
                                {Math.abs(currentStep - index) <= 2 ? (
                                    <img key={title} src={image} alt={title} />
                                ) : null}
                            </div>
                        ))}
                    </SwipeableViews>
                </Box>
            </DialogContent>
            <DialogActions>
                <StepWrapper>
                    <Stepper
                        steps={steps.length}
                        activeStep={currentStep}
                        onClick={setCurrentStep}
                    />
                </StepWrapper>
                <Box
                    display="flex"
                    flexDirection="row-reverse"
                    justifyContent="space-between"
                    ml="0 !important"
                    width={320}>
                    <DialogButton size="large" color="primary" component="div" onClick={handleNext}>
                        {getMessage(CODES.next)}
                    </DialogButton>
                    {!isLastStep && (
                        <DialogButton
                            size="large"
                            color="secondary"
                            component="div"
                            onClick={onSkip}>
                            {getMessage(CODES.skip)}
                        </DialogButton>
                    )}
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default withLocalization(TourModal);
